import React, { useState, useMemo } from "react";
import { useMutation } from "react-query";
import { useIntl } from "react-intl";
import Notiflix from "notiflix";

import { Checkbox, Button, Box } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import PageTitle from "../common/PageTitle";
import BillLogo from "./shared/BillLogo";
import TextFieldGroup from "components/common/TextFieldGroup";
import PayBillsOoredooModal from "./PayBillsOoredooModal";

import {
  getOoredooBillsApiCalled,
  payOoredooBillApiCalled,
} from "shared/endPoints/apiCallers";
import translate from "i18n/translate";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import EditableTextFieldForm from "./shared/InputBills";
import DataTable from "./shared/DataTable";
import GeneralPayModal from "./shared/GeneralPayModal";
import { calcTotalAmount } from "./shared/calcTotalAmount";

const OoredooBills = () => {
  const [number, setNumber] = useState("");
  const [identity, setIdentity] = useState("");
  const [selectedBill, setSelectedBill] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [ooredooPills, setOoredooPills] = useState([]);
  const history = useHistory();

  const intl = useIntl();

  useEffect(() => {
    const { state } = history.location;
    if (state?.number) {
      // Normalize the incoming number even when arriving via deep-link / top-numbers
      // sidebar, so the same prefix/format rules apply as manual entry below.
      const normalized = normalizePalestinianMobile(state.number) || state.number;
      setNumber(normalized);
      setIdentity(state.identity);
      if (!normalized.startsWith("056")) {
        setErrorMessage(
          "This is not an Ooredoo number. Ooredoo numbers start with 056 (e.g. 0568000191)."
        );
        return;
      }
      getOoredooBills({ id: state.identity, number: normalized });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    isLoading,
    mutate: getOoredooBills,
    data: pills,
    reset,
  } = useMutation(getOoredooBillsApiCalled, {
    onSuccess: (res) => {
      if (res?.status === "failed") {
        const errorReason = res?.reason ?? "Something went wrong";
        setErrorMessage(errorReason);
        return;
      }
    },
    onError: () => {
      Notiflix.Notify.failure(`Something went wrong !`, "", "Close");
    },
  });

  useEffect(() => {
    if (pills?.length > 0) {
      setOoredooPills(
        pills.reverse().map((pill) => ({
          ...pill,
          date: pill.date1,
        }))
      );
    }
  }, [pills]);
  // Normalize a user-entered phone number to the local 10-digit Palestinian
  // format (e.g. "0568000191"). Accepts common variations:
  //   "+972 568-000-191", "00972568000191", "972568000191", "568000191" → "0568000191"
  // Returns the cleaned 10-digit string, or null if it can't be coerced.
  const normalizePalestinianMobile = (raw) => {
    if (!raw) return null;
    let s = String(raw).replace(/\D/g, ""); // strip everything except digits
    // strip country code variants (00972, 00970, 972, 970) — Palestinian carriers
    if (/^00?97[02]/.test(s)) s = s.replace(/^00?97[02]/, "");
    // if user dropped the leading 0 (e.g. "568000191"), restore it
    if (s.length === 9 && /^[5][0-9]/.test(s)) s = "0" + s;
    return s.length === 10 && s.startsWith("0") ? s : null;
  };

  const onSubmit = async (value) => {
    if (!value) {
      setErrorMessage("Please enter a phone number");
      return;
    }

    const cleanedNumber = normalizePalestinianMobile(value);
    if (!cleanedNumber) {
      const msg = "Phone number must be a 10-digit Palestinian mobile (e.g. 0568000191)";
      setErrorMessage(msg);
      Notiflix.Notify.failure(msg, "", "Close");
      return;
    }

    // Ooredoo mobile prefix is "056" in Palestine. Jawwal/other (e.g. 059)
    // numbers will silently return zero bills from Toppinex and look like a
    // failure, so reject them up-front with a clear message.
    if (!cleanedNumber.startsWith("056")) {
      const msg = "This is not an Ooredoo number. Ooredoo numbers start with 056 (e.g. 0568000191).";
      setErrorMessage(msg);
      Notiflix.Notify.failure(msg, "", "Close");
      return;
    }

    setNumber(cleanedNumber);
    setSelectedBill([]);
    setOoredooPills([]);
    setErrorMessage("");
    getOoredooBills({ id: identity, number: cleanedNumber });
  };

  const clearSelections = () => {
    setSelectedBill([]);
    reset();
  };
  const totalAmount = parseFloat(calcTotalAmount(selectedBill).toFixed(2));

  return (
    <div>
      <PageTitle title={translate("Ooredoo Bills")} backPage="/pay_bills">
        <BillLogo
          src="https://logowik.com/content/uploads/images/ooredoo3309.logowik.com.webp"
          alt="Ooredoo"
          style={{ marginTop: 20 }}
        />
        <EditableTextFieldForm
          initialValue={number}
          label={translate("Phone number")}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />

        {ooredooPills?.length > 0 && (
          <>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <p>
                {translate("Total Bills Amount")}:{" "}
                <span className="text-info">
                  {totalAmount} {translate("ILS")}
                </span>
              </p>
              <GeneralPayModal
                selectedBill={selectedBill}
                number={number}
                isDisabled={!totalAmount || isLoading}
                clearSelections={clearSelections}
                totalAmount={totalAmount}
                apiCall={payOoredooBillApiCalled}
                provider="ooredoo"
              />
            </div>
            <DataTable
              rows={ooredooPills}
              isLoading={isLoading}
              onRowSelect={setSelectedBill}
              selectedRows={selectedBill}
            />
          </>
        )}
        {pills?.length === 0 && <h5>{translate("No Results Found")}</h5>}
        {errorMessage && <h5 className="text-danger">{errorMessage}</h5>}

        <Box sx={{ display: 'flex', marginTop: 4, marginBottom: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/pay_bills")}
            sx={{ minWidth: 120 }}
          >
            {translate("Back")}
          </Button>
        </Box>
      </PageTitle>
    </div>
  );
};

export default OoredooBills;
